import React, { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Building2, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';
import AccountFitBadge from '@/components/accounts/AccountFitBadge';

export default function TopAccounts({ accounts, isLoading }) {
  const { t } = useTranslation();

  const topAccounts = useMemo(() => {
    return (accounts || [])
      .filter(acc => acc.is_visible !== false && acc.account_fit_score != null && acc.account_fit_score !== '')
      .sort((a, b) => (b.account_fit_score || 0) - (a.account_fit_score || 0))
      .slice(0, 5);
  }, [accounts]);
  
  return (
    <Card className="border-0 shadow-md h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#00C2C2]" />
                <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('topAccounts')}</CardTitle>
            </div>
            <Link to={createPageUrl('Accounts')} className="text-xs font-medium text-[#00C2C2] hover:text-[#00A3A3]">{t('viewAll')}</Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
            {isLoading ? (
                Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)
            ) : topAccounts.map(acc => {
                return (
                    <Link to={createPageUrl(`AccountFinal?id=${acc.id}`)} key={acc.id} className="p-3 rounded-lg bg-gray-50/70 hover:bg-[#00C2C2]/10 transition-colors flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            {acc.logo_url ? (
                                <img src={acc.logo_url} alt={acc.account_name} className="w-8 h-8 object-contain rounded-md border p-0.5 bg-white flex-shrink-0" />
                            ) : (
                                <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md flex-shrink-0">
                                    <Building2 className="w-4 h-4 text-[#5C6B7A]" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#0A1A2F] truncate">{acc.account_name}</p>
                                <p className="text-xs text-[#5C6B7A]">{acc.country}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="inline-block cursor-help">
                                  <AccountFitBadge account={acc} className="text-xs w-28 flex-shrink-0 justify-center" />
                                </div>
                              </TooltipTrigger>
                              {acc.score_rationale && (
                                <TooltipContent className="w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg">
                                  <p className="text-xs font-semibold text-[#00C2C2] mb-1">{t('scoreRationale')}</p>
                                  <p className="text-xs leading-relaxed">{acc.score_rationale}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                    </Link>
                );
            })}
            {!isLoading && topAccounts.length === 0 && (
              <div className="text-center py-6 text-[#5C6B7A]">
                <Building2 className="w-6 h-6 mx-auto mb-1 opacity-50" />
                <p className="text-xs">{t('noAccounts')}</p>
              </div>
            )}
        </div>
      </CardContent>
    </Card>
  );
}